// The Freedom Meter — the heartbeat of the whole game. Passive income vs monthly
// expenses, shown as progress to 100% = Freedom Mode (your money covers your life
// without you working). Every future venture matters only insofar as it moves
// THIS bar. Reusable: pass the numbers in.

import { motion } from 'framer-motion';
import { money } from '../ui/kit.js';
import { CartoonFreedomSky } from '../ui/cartoon.js';

export function FreedomMeter({
  passiveIncome,
  monthlyExpenses,
  freedomPct,
  freedomMode,
  compact,
}: {
  passiveIncome: number;
  monthlyExpenses: number;
  freedomPct: number;
  freedomMode: boolean;
  compact?: boolean;
}) {
  const fill = Math.min(100, Math.max(0, freedomPct));
  const NEON = freedomMode ? '#2fe08a' : '#37b6ff';
  const barColor = freedomMode ? '#2fe08a' : '#ffba2b';

  return (
    <div
      style={{
        background: '#241b42',
        border: `2.5px solid ${NEON}`,
        borderRadius: 18,
        boxShadow: `0 0 0 1px ${NEON}66, 0 0 18px ${NEON}55, 0 6px 18px rgba(0,0,0,0.5)`,
        color: '#f4f1ff',
        overflow: 'hidden',
      }}
    >
      {/* Neon night-city ATMOSPHERE — the skyline lights up as freedom% grows.
          The actual bar and % live below; this strip is decoration that encodes
          the same number with a different sense (height + density). */}
      {!compact && (
        <div style={{ borderBottom: `2.5px solid ${NEON}` }}>
          <CartoonFreedomSky freedomPct={freedomPct} freedomMode={freedomMode} />
        </div>
      )}
      <div style={{ padding: compact ? 14 : 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: freedomMode ? 'var(--up)' : 'var(--text-3)',
          }}
        >
          {freedomMode ? '🔓 FREEDOM UNLOCKED' : 'FREEDOM LEVEL'}
        </span>
        <span className="bk-tnum" style={{ fontSize: compact ? 22 : 30, fontWeight: 900, color: barColor, lineHeight: 1 }}>
          {freedomPct}%
        </span>
      </div>

      {/* the bar */}
      <div style={{ position: 'relative', height: compact ? 14 : 18, borderRadius: 999, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <motion.div
          initial={false}
          animate={{ width: `${fill}%` }}
          transition={{ type: 'spring', stiffness: 90, damping: 20 }}
          style={{
            height: '100%',
            borderRadius: 999,
            background: freedomMode
              ? 'linear-gradient(90deg, var(--up), #34d399)'
              : 'linear-gradient(90deg, var(--brand), var(--brand-strong))',
          }}
        />
        {/* milestone ticks */}
        {[25, 50, 75].map((t) => (
          <div key={t} style={{ position: 'absolute', top: 0, bottom: 0, left: `${t}%`, width: 2, background: 'var(--surface)', opacity: 0.6 }} />
        ))}
      </div>

      {/* income vs expenses */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>💤 MONEY WHILE YOU SLEEP</div>
          <div className="bk-tnum" style={{ fontSize: compact ? 17 : 20, fontWeight: 900, color: 'var(--up)' }}>{money(passiveIncome)}/mo</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>MONEY OUT</div>
          <div className="bk-tnum" style={{ fontSize: compact ? 17 : 20, fontWeight: 900, color: 'var(--down)' }}>{money(monthlyExpenses)}/mo</div>
        </div>
      </div>

      {!compact && (
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5, fontWeight: 700 }}>
          {freedomMode
            ? '🎉 YOU’RE FREE. Money runs itself now.'
            : 'Hit 100%. MONEY WHILE YOU SLEEP beats money out.'}
        </p>
      )}
      </div>
    </div>
  );
}
